<template>
  <section id="pos-id-special-ids" class="pos-id-page">
    <b-card>
      <div class="title d-flex justify-content-between align-items-center flex-wrap">
        <p class="mb-1">多影片匹配白名單</p>
        <div class="d-flex flex-wrap mb-1">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button"
            @click="goManagementCenter"
            >回目錄</b-button
          >
          <b-button
            v-if="!readOnly"
            size="sm"
            variant="primary"
            @click="openCreateDialog"
            >新增白名單</b-button
          >
        </div>
      </div>

      <b-alert v-if="readOnly" show variant="warning" class="mb-3">
        {{ readOnlyHint }}
      </b-alert>

      <div class="pos-id-filters">
        <b-row>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">院線（可多選）</label>
            <el-select
              v-model="filters.circuit_id"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇院線"
              style="width: 100%"
            >
              <el-option
                v-for="item in circuitSelectOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">pos-id</label>
            <div class="d-flex">
              <b-form-input
                v-model="filters.pos_id"
                placeholder="例：13527"
                @keyup.enter="search"
              />
              <b-form-select
                v-model="filters.pos_id_mode"
                :options="posIdModeOptions"
                class="ml-1 flex-shrink-0"
                style="width: 92px"
              />
            </div>
            <div class="pos-id-hint mt-1">清空後查詢會恢復「未過濾」，不會送出空字串。</div>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">影片 id</label>
            <b-form-input
              v-model="filters.film_id"
              placeholder="例：5013"
              @keyup.enter="search"
            />
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">狀態</label>
            <b-form-select v-model="filters.status" :options="statusOptions" />
          </b-col>
        </b-row>
        <div class="pos-id-filters__actions">
          <b-button size="sm" variant="primary" class="mr-1" :disabled="loading" @click="search">
            查詢
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="resetFilters">重置</b-button>
        </div>
      </div>

      <div class="pos-id-table-bar">
        <span class="pos-id-table-bar__title">白名單記錄</span>
        <span class="pos-id-table-bar__hint">
          點擊帶 <i class="el-icon-sort"></i> 的表頭可切換排序（↑ 升序 / ↓ 降序）
        </span>
      </div>

      <div v-loading="loading">
        <b-table
          bordered
          striped
          hover
          responsive
          show-empty
          empty-text="沒有符合條件的白名單"
          :items="rows"
          :fields="displayFields"
        >
          <template #head()="data">
            <pos-id-sortable-header
              :label="data.field.label"
              :sort-key="data.field.sortKey"
              :active-sort-key="filters.sort"
              :direct="filters.direct"
              @sort="handleHeaderSort"
            />
          </template>
          <template #cell(status)="row">
            <b-badge :variant="row.item.status === 'D' ? 'secondary' : 'success'">
              {{ statusText(row.item.status) }}
            </b-badge>
          </template>
          <template #cell(ledger)="row">
            <b-badge :variant="row.item.ledger_exists ? 'success' : 'warning'">
              {{ ledgerText(row.item) }}
            </b-badge>
          </template>
          <template #cell(remarks)="row">
            <span>{{ remarksText(row.item) }}</span>
          </template>
          <template #cell(created_by_name)="row">
            <span>{{ createdByText(row.item) }}</span>
          </template>
          <template #cell(action)="row">
            <span v-if="readOnly" class="text-muted">唯讀</span>
            <b-button
              v-else
              size="sm"
              :variant="row.item.status === 'D' ? 'outline-success' : 'outline-danger'"
              @click="openStatusDialog(row.item)"
            >
              {{ row.item.status === 'D' ? '啟用' : '停用' }}
            </b-button>
          </template>
        </b-table>
        <pos-id-pagination
          :page="filters.page"
          :count="filters.count"
          :total="total"
          @page-change="onPageChange"
          @count-change="onCountChange"
        />
      </div>

      <p class="pos-id-hint mt-3 mb-0">
        本頁只提供「停用 / 啟用」：後端沒有 DELETE 接口，停用後可隨時再啟用；對同一
        (院線 / pos-id / 影片) 三元組再次新增，會原地重新啟用而不新增行。
      </p>
    </b-card>

    <SpecialIdCreateDialog
      v-if="!readOnly"
      ref="createDialog"
      :circuit-options="circuitSelectOptions"
      @created="loadList"
      @goto-exact="onGotoExact"
      @permission-denied="onPermissionDenied"
    />

    <b-modal
      v-model="statusDialog.show"
      centered
      hide-footer
      no-close-on-backdrop
      content-class="pos-id-dialog"
      :title="statusDialogTitle"
      @hidden="resetStatusDialog"
    >
      <div v-if="statusDialog.step === 'form' && statusDialog.row">
        <p class="mb-1">目標白名單：</p>
        <ul class="mb-2">
          <li>院線：{{ statusDialog.row.circuit_id }}</li>
          <li>pos_id：{{ statusDialog.row.pos_id }}</li>
          <li>影片 id：{{ statusDialog.row.film_id }}</li>
          <li>目前狀態：{{ statusText(statusDialog.row.status) }}</li>
          <li>台帳：{{ ledgerText(statusDialog.row) }}</li>
          <li>變更後狀態：{{ statusText(statusDialog.nextStatus) }}</li>
        </ul>

        <b-alert show :variant="statusDialog.nextStatus === 'D' ? 'warning' : 'info'">
          <span v-if="statusDialog.nextStatus === 'D'">
            停用後匯入不再命中此白名單；該 pos-id 若再出現多片共用，會退回「衝突加
            Z」流程。既有票房明細不受影響（記錄未移除，可隨時啟用）。
          </span>
          <span v-else>
            啟用後匯入會重新按此白名單命中該影片。
          </span>
        </b-alert>

        <b-form-group label="緣由（寫入變更記錄，必填）" label-for="pos-id-status-reason">
          <b-form-textarea
            id="pos-id-status-reason"
            v-model="statusDialog.reason"
            rows="2"
            placeholder="例：活動結束"
          />
        </b-form-group>

        <b-form-checkbox
          v-if="statusDialog.nextStatus === 'D'"
          v-model="statusDialog.confirmChecked"
        >
          我已確認要停用此白名單（此為高影響操作，匯入將不再命中）
        </b-form-checkbox>

        <div class="float-right mt-2">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="mr-1"
            :disabled="statusDialog.submitting"
            @click="closeStatusDialog"
            >取消</b-button
          >
          <b-button
            size="sm"
            :variant="statusDialog.nextStatus === 'D' ? 'danger' : 'success'"
            :disabled="!canSubmitStatus"
            @click="submitStatus"
          >
            {{ statusDialog.nextStatus === 'D' ? '確認停用' : '確認啟用' }}
          </b-button>
        </div>
        <div class="clearfix"></div>
      </div>

      <div v-else>
        <b-alert show :variant="statusDialog.changed ? 'success' : 'warning'">
          {{ resultHeadline }}
        </b-alert>
        <div v-if="statusDialog.resultNotes.length > 0">
          <p class="mb-1">後端 notes：</p>
          <ul class="mb-2">
            <li v-for="(note, index) in statusDialog.resultNotes" :key="index">
              {{ note }}
            </li>
          </ul>
        </div>
        <p v-if="statusDialog.auditId" class="mb-1">
          變更記錄 id：{{ statusDialog.auditId }}
        </p>
        <p v-if="!statusDialog.changed" class="mb-1 text-muted">
          未寫入變更記錄（狀態與目前相同）。
        </p>
        <div class="float-right mt-2">
          <b-button size="sm" variant="primary" @click="closeStatusDialog"
            >關閉</b-button
          >
        </div>
        <div class="clearfix"></div>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { getFilmCircuitSpecialIds, updateFilmCircuitSpecialId } from '@/api/posId'
import { getCircuitList } from '@/api/circuit'
import {
  buildSpecialIdQuery,
  buildSpecialIdStatusPayload,
  normalizeNotes,
  resolvePosIdError,
  SPECIAL_ID_STATUS_OPTIONS,
  POS_ID_READ_ONLY_HINT
} from '@/utils/posId'
import SpecialIdCreateDialog from './components/SpecialIdCreateDialog.vue'
import PosIdPagination from './components/PosIdPagination.vue'
import PosIdSortableHeader from './components/PosIdSortableHeader.vue'

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function firstText(...values) {
  for (let index = 0; index < values.length; index++) {
    if (hasText(values[index])) {
      return String(values[index]).trim()
    }
  }
  return ''
}

export default {
  name: 'FilmCircuitSpecialIdPage',
  components: {
    SpecialIdCreateDialog,
    PosIdPagination,
    PosIdSortableHeader
  },
  data() {
    return {
      readOnly: false,
      readOnlyHint: POS_ID_READ_ONLY_HINT,
      loading: false,
      rows: [],
      total: 0,
      circuitOptions: [],
      statusOptions: SPECIAL_ID_STATUS_OPTIONS,
      posIdModeOptions: [
        { text: '前綴', value: 'prefix' },
        { text: '精確', value: 'exact' }
      ],
      filters: {
        circuit_id: [],
        pos_id: '',
        pos_id_mode: 'prefix',
        film_id: '',
        status: '',
        page: 1,
        count: 20,
        sort: 'id',
        direct: 'desc'
      },
      tableFields: [
        { key: 'id', label: 'id', class: 'text-nowrap', sortKey: 'id' },
        { key: 'circuit_id', label: '院線', sortKey: 'circuit_id' },
        { key: 'pos_id', label: 'pos_id', sortKey: 'pos_id' },
        { key: 'film_id', label: '影片 id', sortKey: 'film_id' },
        { key: 'status', label: '狀態' },
        { key: 'ledger', label: '台帳' },
        { key: 'remarks', label: '備註' },
        { key: 'created_by_name', label: '建立人' },
        { key: 'created_at', label: '建立時間', sortKey: 'created_at' },
        { key: 'updated_at', label: '更新時間', sortKey: 'updated_at' },
        { key: 'action', label: '操作' }
      ],
      statusDialog: {
        show: false,
        step: 'form',
        row: null,
        nextStatus: '',
        reason: '',
        confirmChecked: false,
        submitting: false,
        changed: null,
        auditId: null,
        resultNotes: []
      }
    }
  },
  computed: {
    circuitSelectOptions() {
      return this.circuitOptions
    },
    // 當前排序列的表頭以 variant=info 高亮（配合 PosIdSortableHeader 的 ↑ / ↓ 標誌）
    displayFields() {
      return this.tableFields.map((field) => {
        if (field.sortKey && field.sortKey === this.filters.sort) {
          return Object.assign({}, field, { variant: 'info' })
        }
        return field
      })
    },
    statusDialogTitle() {
      if (this.statusDialog.step === 'done') {
        return this.statusDialog.changed ? '變更結果' : '未產生變更'
      }
      return this.statusDialog.nextStatus === 'D' ? '停用白名單' : '啟用白名單'
    },
    canSubmitStatus() {
      if (this.statusDialog.submitting) {
        return false
      }
      if (String(this.statusDialog.reason).trim() === '') {
        return false
      }
      if (this.statusDialog.nextStatus === 'D' && this.statusDialog.confirmChecked !== true) {
        return false
      }
      return true
    },
    resultHeadline() {
      if (this.statusDialog.changed !== true) {
        return '狀態本來就一樣，未產生變更記錄。'
      }
      return this.statusDialog.nextStatus === 'D' ? '已停用此白名單。' : '已啟用此白名單。'
    }
  },
  created() {
    this.loadList()
    this.loadCircuitOptions()
  },
  methods: {
    goManagementCenter() {
      this.$router.push({ name: 'management-center' })
    },
    search() {
      this.filters.page = 1
      this.loadList()
    },
    onPageChange(page) {
      this.filters.page = Number(page) || 1
      this.loadList()
    },
    onCountChange(count) {
      this.filters.count = Number(count) || 20
      this.filters.page = 1
      this.loadList()
    },
    // 表頭點擊排序（全站習慣）：點同一欄切換升降，改點另一欄時回到預設「新 → 舊」
    handleHeaderSort(sortKey) {
      if (!sortKey) {
        return
      }
      if (this.filters.sort === sortKey) {
        this.filters.direct = this.filters.direct === 'asc' ? 'desc' : 'asc'
      } else {
        this.filters.sort = sortKey
        this.filters.direct = 'desc'
      }
      this.filters.page = 1
      this.loadList()
    },
    resetFilters() {
      this.filters.circuit_id = []
      this.filters.pos_id = ''
      this.filters.pos_id_mode = 'prefix'
      this.filters.film_id = ''
      this.filters.status = ''
      this.filters.page = 1
      this.filters.count = 20
      this.filters.sort = 'id'
      this.filters.direct = 'desc'
      this.loadList()
    },
    loadList() {
      this.loading = true
      // buildSpecialIdQuery：搜尋框清空時整個 key 都不送（送空值後端會回空集）
      const params = buildSpecialIdQuery(this.filters)
      getFilmCircuitSpecialIds(params)
        .then((res) => {
          const list = res && Array.isArray(res.data) ? res.data : []
          this.rows = list
          this.total = res && typeof res.total === 'number' ? res.total : list.length
          this.mergeCircuitOptions(list)
        })
        .catch((error) => {
          this.rows = []
          this.total = 0
          this.handleError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadCircuitOptions() {
      // 既有院線來源（GET /api/circuit）作為建議清單；失敗時仍可由列表已載入的院線兜底
      getCircuitList({ page: 1, count: 200 })
        .then((res) => {
          const payload = res ? res.data : null
          let rows = []
          if (Array.isArray(payload)) {
            rows = payload
          } else if (payload && Array.isArray(payload.data)) {
            rows = payload.data
          }
          this.mergeCircuitOptions(rows)
        })
        .catch(() => {
          // 院線清單屬輔助資料，載入失敗不影響主流程
        })
    },
    mergeCircuitOptions(rows) {
      const map = {}
      this.circuitOptions.forEach((option) => {
        map[option.value] = option.text
      })
      const list = Array.isArray(rows) ? rows : []
      list.forEach((row) => {
        if (!row) {
          return
        }
        const value = firstText(row.circuit_key, row.circuit_id, row.id)
        if (value === '') {
          return
        }
        const label = firstText(row.full_name, row.circuit_name)
        if (label === '') {
          if (map[value] === undefined) {
            map[value] = value
          }
        } else {
          map[value] = `${value} · ${label}`
        }
      })
      this.circuitOptions = Object.keys(map)
        .sort()
        .map((value) => ({ value, text: map[value] }))
    },
    handleError(error) {
      const info = resolvePosIdError(error)
      if (info.isPermissionDenied) {
        this.readOnly = true
        this.$message.warning(POS_ID_READ_ONLY_HINT)
      } else {
        this.$message.error(info.message)
      }
      return info
    },
    onPermissionDenied() {
      this.readOnly = true
      this.statusDialog.show = false
      this.$message.warning(POS_ID_READ_ONLY_HINT)
    },
    onGotoExact(payload) {
      const circuit = payload && payload.circuit_id ? String(payload.circuit_id) : ''
      if (circuit !== '') {
        this.mergeCircuitOptions([{ circuit_key: circuit }])
        this.filters.circuit_id = [circuit]
      }
      this.filters.pos_id_mode = 'exact'
      this.filters.pos_id = payload && payload.pos_id ? String(payload.pos_id) : ''
      this.filters.status = '*'
      this.search()
      if (this.$refs.createDialog && typeof this.$refs.createDialog.close === 'function') {
        this.$refs.createDialog.close()
      }
    },
    openCreateDialog() {
      if (this.readOnly) {
        return
      }
      if (this.$refs.createDialog && typeof this.$refs.createDialog.open === 'function') {
        this.$refs.createDialog.open()
      }
    },
    openStatusDialog(row) {
      if (this.readOnly || !row) {
        return
      }
      this.statusDialog.step = 'form'
      this.statusDialog.row = row
      this.statusDialog.nextStatus = row.status === 'D' ? '' : 'D'
      this.statusDialog.reason = ''
      this.statusDialog.confirmChecked = false
      this.statusDialog.submitting = false
      this.statusDialog.changed = null
      this.statusDialog.auditId = null
      this.statusDialog.resultNotes = []
      this.statusDialog.show = true
    },
    closeStatusDialog() {
      this.statusDialog.show = false
    },
    resetStatusDialog() {
      this.statusDialog.step = 'form'
      this.statusDialog.row = null
      this.statusDialog.reason = ''
      this.statusDialog.confirmChecked = false
      this.statusDialog.submitting = false
      this.statusDialog.changed = null
      this.statusDialog.auditId = null
      this.statusDialog.resultNotes = []
    },
    submitStatus() {
      if (!this.canSubmitStatus || !this.statusDialog.row) {
        return
      }
      const row = this.statusDialog.row
      const nextStatus = this.statusDialog.nextStatus
      this.statusDialog.submitting = true
      // 注意：後端 PUT /film-circuit-special-ids/{id} 不支援 dry_run（dry_run=1 仍會真實寫入），
      // 因此停用 / 啟用改為「本地確認（含必填緣由與停用二次確認）→ 單次正式提交」，
      // 且契約層 buildSpecialIdStatusPayload(status, reason) 刻意不產生 dry_run 欄位。
      updateFilmCircuitSpecialId(
        row.id,
        buildSpecialIdStatusPayload(nextStatus, this.statusDialog.reason)
      )
        .then((res) => {
          const data = res && res.data && typeof res.data === 'object' ? res.data : {}
          const changed = data.changed === true
          this.statusDialog.changed = changed
          this.statusDialog.auditId =
            data.audit_id === undefined ? null : data.audit_id
          this.statusDialog.resultNotes = normalizeNotes(data.notes)
          this.statusDialog.step = 'done'
          if (changed) {
            this.$message.success(nextStatus === 'D' ? '已停用此白名單' : '已啟用此白名單')
          } else {
            // changed=false：狀態本來就一樣，後端不會寫空審計，不要提示「已變更」
            this.$message.warning('狀態本來就一樣，未產生變更記錄')
          }
          this.loadList()
        })
        .catch((error) => {
          const info = this.handleError(error)
          if (info.isPermissionDenied || info.code === 10010) {
            this.statusDialog.show = false
          }
          if (info.code === 10010) {
            this.loadList()
          }
        })
        .finally(() => {
          this.statusDialog.submitting = false
        })
    },
    statusText(status) {
      return status === 'D' ? '已停用' : '有效'
    },
    ledgerText(row) {
      if (!row || row.ledger_exists !== true) {
        return '缺失'
      }
      if (row.ledger_id === null || row.ledger_id === undefined || row.ledger_id === '') {
        return '已有'
      }
      return `已有 #${row.ledger_id}`
    },
    remarksText(row) {
      if (!row || row.remarks === null || row.remarks === undefined || row.remarks === '') {
        return '—'
      }
      return row.remarks
    },
    createdByText(row) {
      if (!row) {
        return '—'
      }
      const value = row.created_by_name
      if (value === null || value === undefined || value === '') {
        return '—'
      }
      return value
    }
  }
}
</script>

<style lang="scss" scoped>
// 標題列 / 篩選區 / 表格列 / 排序表頭 / badge 等共用版面樣式由 src/assets/scss/pos-id.scss 統一提供
.handle-button {
  margin-right: 8px;
}
</style>
